// client/src/coms/views/canvas_parts/CanvasSurfaceMediaControls.js
// Medium / type selection for the drawing tools (pencil media, pen types,
// eraser types) and a preview of how the chosen tool meets the document's
// current paper. The preview uses the document's own tooth field and the same
// contact formula as the GPU tools (surfaceContact.js): a light-to-heavy
// pressure ramp, and the same stroke after three passes (valleys fill).
import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  ERASER_TYPES,
  PEN_MEDIA,
  PENCIL_MEDIA,
  calibrateProfile,
  contactAmount,
  contactProfileFor,
  getSurfaceMedia,
  getToothField,
  setSurfaceMedia,
  subscribeSurfaceMedia,
  subscribeToothField,
} from '../../canvas/paint-engine/integration/surfaceContact';
import { renderPencilPreview } from './canvas_room_view_parts/dryMediaPreview';

// Pencil previews are drawn by the real pencil code (stamps + medium shader).
const PENCIL_PREVIEW_COLOUR = { graphite: '#2a2a30', mechanical: '#2a2a30', coloured: '#2e5aa8', charcoal: '#1c1a1a', pastel: '#c0503c', chalk: '#e9e4d6', crayon: '#c23a2e' };

const PREVIEW_WIDTH = 252;
const PREVIEW_HEIGHT = 104;

const labelStyle = { fontSize: 10, fontWeight: 950, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)' };

const useMedia = () => useSyncExternalStore(subscribeSurfaceMedia, getSurfaceMedia, getSurfaceMedia);
const useTooth = () => useSyncExternalStore(subscribeToothField, getToothField, getToothField);

// Normalised height (standard deviations) at preview pixel (x, y).
function toothAt(tooth, x, y) {
  if (!tooth) return 0;
  // Document px per tooth cell: 1 for the fine paper tile, 2 for the paint relief.
  const perCell = tooth.pxPerCell || 2;
  const wrap = (v, n) => (tooth.repeat ? ((v % n) + n) % n : Math.min(n - 1, Math.max(0, v)));
  const cx = wrap(x / perCell, tooth.width);
  const cy = wrap(y / perCell, tooth.height);
  const x0 = Math.floor(cx); const y0 = Math.floor(cy);
  const x1 = tooth.repeat ? (x0 + 1) % tooth.width : Math.min(tooth.width - 1, x0 + 1);
  const y1 = tooth.repeat ? (y0 + 1) % tooth.height : Math.min(tooth.height - 1, y0 + 1);
  const fx = cx - x0; const fy = cy - y0;
  const b = tooth.bytes;
  const v = (b[y0 * tooth.width + x0] * (1 - fx) + b[y0 * tooth.width + x1] * fx) * (1 - fy)
    + (b[y1 * tooth.width + x0] * (1 - fx) + b[y1 * tooth.width + x1] * fx) * fy;
  return (v / 255 - 0.5) * 6;
}

// Renders one preview into an ImageData-sized RGBA buffer (exported for tests).
export function renderSurfacePreview(buffer, width, height, { kind, media, tooth, grade = 0, tilt = 0 }) {
  const dryInk = [38, 38, 44];
  const calibration = tooth ? tooth.calibration : null;
  const profile = calibrateProfile(contactProfileFor({ tool: kind === 'eraser' ? 'eraser' : 'pen', preset: kind === 'pencil' ? 'pencil' : kind === 'airbrush' ? 'airbrush' : 'pen', media, grade, tilt }), calibration);
  const strength = tooth ? Math.max(0, Math.min(1, tooth.strength * profile.sensitivity)) : 0;
  const graphite = calibrateProfile(contactProfileFor({ tool: 'pen', preset: 'pencil', media: { ...media, pencil: 'graphite' } }), calibration);
  const graphiteStrength = tooth ? Math.max(0, Math.min(1, tooth.strength * graphite.sensitivity)) : 0;
  const bleed = profile.bleed ? (profile.bleed / 100) * Math.min(1.5, tooth ? tooth.absorbency : 0) : 0;
  const radius = kind === 'pencil' ? 6 + 4 * tilt : kind === 'airbrush' ? 10 : 6;
  const rows = [height * 0.3, height * 0.72];
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const z = toothAt(tooth, x, y);
      // Paper: lit relief (visual only).
      const paper = 238 + Math.max(-1, Math.min(1, z / 3)) * 10 * (tooth ? tooth.strength : 0);
      let r = paper; let g = paper - 2; let b = paper - 8;
      const pressure = 0.12 + 0.88 * (x / (width - 1));
      let cover = 0;
      let lift = 0;
      for (let row = 0; row < 2; row += 1) {
        const centre = rows[row] + Math.sin(x / 19 + row) * 5;
        const d = Math.abs(y - centre) / (radius * (kind === 'pencil' ? 1 : 0.6 + 0.6 * pressure));
        const edge = Math.max(0.02, 0.25 + bleed);
        const mask = 1 - Math.max(0, Math.min(1, (d - (1 - edge)) / edge));
        if (mask <= 0) continue;
        if (kind === 'eraser') {
          lift = Math.max(lift, Math.min(1, mask * contactAmount(z, pressure, profile, strength) * (profile.opacity ?? 1)));
        } else {
          const once = Math.min(1, mask * 0.82 * contactAmount(z, pressure, profile, strength));
          cover = Math.max(cover, row === 0 ? once : 1 - (1 - once) ** 3);
        }
      }
      if (kind === 'eraser') {
        // Graphite laid over the whole preview (two passes), then the eraser strokes.
        const laid = 1 - (1 - 0.8 * contactAmount(z, 0.8, graphite, graphiteStrength)) ** 2;
        cover = laid * (1 - lift);
      }
      r = r * (1 - cover) + dryInk[0] * cover;
      g = g * (1 - cover) + dryInk[1] * cover;
      b = b * (1 - cover) + dryInk[2] * cover;
      const i = 4 * (y * width + x);
      buffer[i] = r; buffer[i + 1] = g; buffer[i + 2] = b; buffer[i + 3] = 255;
    }
  }
  return buffer;
}

function SurfaceToolPreview({ kind, media, grade, tilt }) {
  const tooth = useTooth();
  const canvasRef = useRef(null);
  const [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const context = canvas.getContext('2d');
    if (!context) return undefined;
    if (kind === 'pencil') {
      // Real medium implementation, rendered when idle and cached.
      return renderPencilPreview(canvas, { media, grade, size: 10, color: PENCIL_PREVIEW_COLOUR[media.pencil], tooth, onUnavailable: () => setUnavailable(true) });
    }
    const image = context.createImageData(PREVIEW_WIDTH, PREVIEW_HEIGHT);
    renderSurfacePreview(image.data, PREVIEW_WIDTH, PREVIEW_HEIGHT, { kind, media, tooth, grade, tilt });
    context.putImageData(image, 0, 0);
    return undefined;
  }, [kind, media, tooth, grade, tilt]);
  if (unavailable) {
    return <div style={{ fontSize: 10, lineHeight: 1.4, color: 'rgba(255,255,255,0.5)' }}>Preview unavailable (WebGL is off): draw on the canvas to see this medium.</div>;
  }
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <canvas
        ref={canvasRef}
        width={PREVIEW_WIDTH}
        height={PREVIEW_HEIGHT}
        data-testid={`surface-preview-${kind}`}
        aria-label={kind === 'pencil'
          ? 'The selected medium on this paper: light, medium and heavy pressure, side shading (right) and three passes (bottom)'
          : 'How this tool meets the paper: pressure from light (left) to heavy (right); bottom, three passes'}
        style={{ width: '100%', maxWidth: PREVIEW_WIDTH, borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)' }}
      />
      <div style={{ fontSize: 10, lineHeight: 1.4, color: 'rgba(255,255,255,0.5)' }}>
        {tooth && tooth.strength > 0.02
          ? kind === 'eraser'
            ? 'Paper tooth: light pressure lifts from the raised grain first; press harder to reach the valleys.'
            : kind === 'pencil'
              ? 'Light, medium and heavy pressure, side shading (right) and three passes (bottom) on this paper.'
              : 'Paper tooth: light pressure (left) catches the raised grain, heavy pressure (right) and repeated passes (bottom) fill the valleys.'
          : 'This paper has no tooth: the tool draws evenly.'}
      </div>
    </div>
  );
}

const OPTIONS = { pencil: PENCIL_MEDIA, pen: PEN_MEDIA, eraser: ERASER_TYPES };
const LABELS = { pencil: 'Medium', pen: 'Pen type', eraser: 'Eraser type' };

// kind: 'pencil' | 'pen' | 'eraser' | 'airbrush'. grade: -8..8, elevation in degrees.
export default function CanvasSurfaceMediaControls({ kind, pencilGrade = 0, pencilElevation = 90 }) {
  const media = useMedia();
  const options = OPTIONS[kind];
  const tilt = kind === 'pencil' ? Math.max(0, Math.min(1, (90 - Math.max(15, Math.min(90, Number(pencilElevation) || 90))) / 75)) : 0;
  return (
    <div data-testid="surface-media-controls" style={{ display: 'grid', gap: 7, minWidth: 0 }}>
      {options ? (
        <label style={{ display: 'grid', gap: 5, minWidth: 0 }}>
          <span style={labelStyle}>{LABELS[kind]}</span>
          <select
            value={media[kind]}
            onChange={(event) => setSurfaceMedia({ [kind]: event.target.value })}
            style={{ width: '100%', minHeight: 32, borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.24)', color: 'rgba(255,255,255,0.92)', padding: '0 9px', fontSize: 12, fontWeight: 800, outline: 'none' }}
          >
            {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </label>
      ) : null}
      <SurfaceToolPreview kind={kind} media={media} grade={(Number(pencilGrade) || 0) / 8} tilt={tilt} />
    </div>
  );
}
